import { useEffect, useState } from "react";
import { useQueries } from "@tanstack/react-query";
import { api, extractErrorMessage } from "@/api/client";
import { queryKeys } from "@/api/queryKeys";
import { useUpdateVarianta } from "@/api/hooks/useVarianty";
import type {
  CenaNahlad,
  CenaNahladVysledok,
  DataResponse,
  Varianta,
} from "@/api/schemas";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/use-toast";
import { formatCisloBezNul, formatEur, parseDecimal } from "@/lib/format";

/** Podmienky jedného odboru — varianty, ktoré idú do ceny zákazky. */
export interface OdborPonuky {
  kluc: "nabytok" | "tovar";
  nazov: string;
  /** Pri zákazke bez výpočtu všetky časti výroby — podmienky dostanú všetky. */
  varianty: Varianta[];
}

function cislo(v: string): number {
  return Number(v.replace(",", ".").trim());
}

function platnyKoef(v: string): boolean {
  const n = cislo(v);
  return v.trim() !== "" && Number.isFinite(n) && n > 0 && n <= 10;
}

function platnaZlava(v: string): boolean {
  const n = cislo(v || "0");
  return Number.isFinite(n) && n >= 0 && n < 100;
}

/**
 * Obchodné podmienky ponuky (KOLO 231) — koeficient a zľava zvlášť pre
 * nábytok a tovar. User: *„pre obchodníka je podstatná cena — aby vedel
 * meniť, dávať koeficient alebo zľavu"*.
 *
 * Koeficient násobí súčet zostáv (rozpočet) pred zľavou a DPH; zákazník ho
 * nevidí, ceny riadkov v ponuke ho už majú. Náhľad počíta server
 * (`POST /varianty/{id}/cena-nahlad`) — nič neuloží, kým nie je Uložiť.
 */
export function PodmienkyPonukyDialog({
  zakazkaId,
  odbory,
  open,
  onOpenChange,
}: {
  zakazkaId: string;
  odbory: OdborPonuky[];
  open: boolean;
  onOpenChange: (o: boolean) => void;
}): JSX.Element {
  const update = useUpdateVarianta(zakazkaId);
  const [hodnoty, setHodnoty] = useState<Record<string, { koef: string; zlava: string }>>({});
  const [uklada, setUklada] = useState(false);

  useEffect(() => {
    if (!open) return;
    const nove: Record<string, { koef: string; zlava: string }> = {};
    for (const o of odbory) {
      const v = o.varianty[0];
      nove[o.kluc] = {
        koef: formatCisloBezNul(v?.koef_ponuky ?? "1"),
        zlava: formatCisloBezNul(v?.sleva_pct ?? "0"),
      };
    }
    setHodnoty(nove);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const platne = odbory.every((o) => {
    const h = hodnoty[o.kluc];
    return h != null && platnyKoef(h.koef) && platnaZlava(h.zlava);
  });

  // Náhľad každej varianty v cene — hypotéza je v kľúči, staré odpovede
  // zahodí TanStack sám (ten istý vzor ako pult marží)
  const poziadavky = odbory.flatMap((o) =>
    o.varianty.map((v) => {
      const h = hodnoty[o.kluc];
      const hypoteza: CenaNahlad = {
        koef_ponuky: h && platnyKoef(h.koef) ? parseDecimal(h.koef) : undefined,
        sleva_pct: h && platnaZlava(h.zlava) ? parseDecimal(h.zlava || "0") : undefined,
      };
      return { odbor: o.kluc, variantaId: v.id, hypoteza };
    }),
  );
  const nahlady = useQueries({
    queries: poziadavky.map((p) => ({
      queryKey: queryKeys.varianty.cenaNahlad(p.variantaId, p.hypoteza),
      queryFn: async () => {
        const { data } = await api.post<DataResponse<CenaNahladVysledok>>(
          `/varianty/${p.variantaId}/cena-nahlad`,
          p.hypoteza,
        );
        return data;
      },
      enabled: open,
      placeholderData: (predosla: DataResponse<CenaNahladVysledok> | undefined) => predosla,
    })),
  });

  function suma(odbor: string, pole: "cena_s_dph" | "rozpocet_s_dph"): number | null {
    let spolu = 0;
    let ma = false;
    poziadavky.forEach((p, i) => {
      if (p.odbor !== odbor) return;
      const n = nahlady[i]?.data?.data;
      if (!n) return;
      spolu += Number(n[pole] ?? 0);
      ma = true;
    });
    return ma ? spolu : null;
  }

  async function uloz(): Promise<void> {
    if (!platne) return;
    setUklada(true);
    try {
      for (const o of odbory) {
        const h = hodnoty[o.kluc]!;
        for (const v of o.varianty) {
          await update.mutateAsync({
            id: v.id,
            data: {
              koef_ponuky: parseDecimal(h.koef),
              sleva_pct: parseDecimal(h.zlava || "0"),
            },
          });
        }
      }
      onOpenChange(false);
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Podmienky ponuky sa neuložili",
        description: extractErrorMessage(err),
      });
    } finally {
      setUklada(false);
    }
  }

  const spoluPonuka = odbory.reduce<number | null>((acc, o) => {
    const s = suma(o.kluc, "cena_s_dph");
    return s == null ? acc : (acc ?? 0) + s;
  }, null);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Podmienky ponuky</DialogTitle>
          <DialogDescription>
            Koeficient násobí rozpočet pred zľavou a DPH — zákazník ho nevidí, ceny
            riadkov v ponuke ho už majú. Zľava je v ponuke vlastným riadkom.
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            void uloz();
          }}
        >
          {odbory.map((o) => {
            const h = hodnoty[o.kluc] ?? { koef: "", zlava: "" };
            const rozpocet = suma(o.kluc, "rozpocet_s_dph");
            const ponuka = suma(o.kluc, "cena_s_dph");
            return (
              <fieldset key={o.kluc} className="space-y-2 rounded-md border px-3 pb-3 pt-2">
                <legend className="px-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {o.nazov}
                  {o.varianty.length > 1 ? ` · ${o.varianty.length} časti` : ""}
                </legend>
                <div className="flex gap-3">
                  <div className="w-28 space-y-1">
                    <Label htmlFor={`pp-koef-${o.kluc}`}>Koeficient ×</Label>
                    <Input
                      id={`pp-koef-${o.kluc}`}
                      value={h.koef}
                      onChange={(e) =>
                        setHodnoty((x) => ({ ...x, [o.kluc]: { ...h, koef: e.target.value } }))
                      }
                      inputMode="decimal"
                      aria-invalid={!platnyKoef(h.koef)}
                      className="font-mono"
                    />
                  </div>
                  <div className="w-28 space-y-1">
                    <Label htmlFor={`pp-zlava-${o.kluc}`}>Zľava %</Label>
                    <Input
                      id={`pp-zlava-${o.kluc}`}
                      value={h.zlava}
                      onChange={(e) =>
                        setHodnoty((x) => ({ ...x, [o.kluc]: { ...h, zlava: e.target.value } }))
                      }
                      inputMode="decimal"
                      aria-invalid={!platnaZlava(h.zlava)}
                      className="font-mono"
                    />
                  </div>
                  <div className="ml-auto whitespace-nowrap text-right text-xs text-muted-foreground">
                    <div>
                      Rozpočet <span className="font-mono">{formatEur(rozpocet)}</span>
                    </div>
                    <div className="text-sm text-foreground" data-testid={`pp-ponuka-${o.kluc}`}>
                      Ponuka <b className="font-mono">{formatEur(ponuka)}</b>
                    </div>
                  </div>
                </div>
              </fieldset>
            );
          })}
          <p className="text-right text-sm">
            Zákazka spolu s DPH{" "}
            <b className="font-mono" data-testid="pp-spolu">
              {formatEur(spoluPonuka)}
            </b>
          </p>
          {!platne ? (
            <p className="text-xs text-destructive">
              Koeficient je väčší ako 0 a najviac 10, zľava od 0 do 99 %.
            </p>
          ) : null}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Zrušiť
            </Button>
            <Button type="submit" disabled={!platne || uklada}>
              Uložiť
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
